import { FlatInjectionObj } from '../../../../../../../../store/types/prescription';
import { INJECTION_COLUMN } from '../../../../../../../../utils/enums';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getInjectionElements } from '../../../../../../../../store/selectors/prescriptionPad';
import { upsertInjection } from '../../../../../../../../store/actions/prescriptionPad';
import CalculatorRegularIcon from '../../../../../../../../icons/regular/calculator-regular';
import { CalculatorRightPopUp } from '../../../CalculatorRightPopUp';
import { CALCULATORS_NAMES } from '../../../../calculatorsTypes';
import { Popover } from 'antd';
import InfusionRatePopup from './infusionRateItems/InfusionRatePopup';
import { INJECTIONS_TABLE_CONTAINER } from '../InjectionTable';

const InjectionInfusionRate = ({
    injection,
    columnKey,
    isDragMode,
}: {
    injection: FlatInjectionObj;
    columnKey: INJECTION_COLUMN;
    isDragMode: boolean;
}): JSX.Element => {
    const elements = useSelector(getInjectionElements);
    const dispatch = useDispatch();
    const { infusion_rate } = injection;
    const { iv_drop_rate, iv_rate, infusion_time } = infusion_rate || {};
    const [selectedDropFlowRate, setSelectedDropFlowRate] = useState<string>(
        iv_drop_rate?.custom || '',
    );
    const [isCalculatorOpen, setIsCalculatorOpen] = useState<boolean>(false);
    const [selectedFlowRate, setSelectedFlowRate] = useState<string>(iv_rate?.custom || '');
    const [selectedFlowTime, setSelectedFlowTime] = useState<string>(infusion_time?.custom || '');
    const [showInfusionPopup, setShowInfusionPopup] = useState<boolean>(false);

    const handleIvRateChanges = ({
        value,
        type,
    }: {
        value: string;
        type: 'iv-drop-rate' | 'iv-rate' | 'flow-time';
    }) => {
        const { is_added_drug, is_tapering_dose, currentIndex, mainIndex } = injection;
        const clonedElement = elements[mainIndex];
        const realValue = value.split(' ')[0] ? value : '';
        if (type === 'iv-drop-rate') {
            setSelectedDropFlowRate(realValue);
            if (is_added_drug) {
                if (clonedElement.added_drug?.[currentIndex]) {
                    clonedElement.added_drug[currentIndex].infusion_rate = {
                        ...(clonedElement.added_drug[currentIndex].infusion_rate || {}),
                        iv_drop_rate: {
                            custom: realValue,
                        },
                    };
                }
            } else if (is_tapering_dose) {
                if (clonedElement.tapering_dose?.[currentIndex]) {
                    clonedElement.tapering_dose[currentIndex].infusion_rate = {
                        ...(clonedElement.tapering_dose[currentIndex].infusion_rate || {}),
                        iv_drop_rate: {
                            custom: realValue,
                        },
                    };
                }
            } else {
                if (clonedElement) {
                    clonedElement.infusion_rate = {
                        ...(clonedElement.infusion_rate || {}),
                        iv_drop_rate: {
                            custom: realValue,
                        },
                    };
                }
            }
        } else if (type === 'iv-rate') {
            setSelectedFlowRate(realValue);
            if (is_added_drug) {
                if (clonedElement.added_drug?.[currentIndex]) {
                    clonedElement.added_drug[currentIndex].infusion_rate = {
                        ...(clonedElement.added_drug[currentIndex].infusion_rate || {}),
                        iv_rate: {
                            custom: realValue,
                        },
                    };
                }
            } else if (is_tapering_dose) {
                if (clonedElement.tapering_dose?.[currentIndex]) {
                    clonedElement.tapering_dose[currentIndex].infusion_rate = {
                        ...(clonedElement.tapering_dose[currentIndex].infusion_rate || {}),
                        iv_rate: {
                            custom: realValue,
                        },
                    };
                }
            } else {
                if (clonedElement) {
                    clonedElement.infusion_rate = {
                        ...(clonedElement.infusion_rate || {}),
                        iv_rate: {
                            custom: realValue,
                        },
                    };
                }
            }
        } else if (type === 'flow-time') {
            setSelectedFlowTime(realValue);
            if (is_added_drug) {
                if (clonedElement.added_drug?.[currentIndex]) {
                    clonedElement.added_drug[currentIndex].infusion_rate = {
                        ...(clonedElement.added_drug[currentIndex].infusion_rate || {}),
                        infusion_time: {
                            custom: value,
                        },
                    };
                }
            } else if (is_tapering_dose) {
                if (clonedElement.tapering_dose?.[currentIndex]) {
                    clonedElement.tapering_dose[currentIndex].infusion_rate = {
                        ...(clonedElement.tapering_dose[currentIndex].infusion_rate || {}),
                        infusion_time: {
                            custom: realValue,
                        },
                    };
                }
            } else {
                if (clonedElement) {
                    clonedElement.infusion_rate = {
                        ...(clonedElement.infusion_rate || {}),
                        infusion_time: {
                            custom: realValue,
                        },
                    };
                }
            }
        }
        dispatch(upsertInjection(clonedElement, mainIndex));
    };
    return isDragMode && (injection.is_added_drug || injection.is_tapering_dose) ? (
        <></>
    ) : (
        <>
            <Popover
                placement="right"
                trigger="click"
                overlayInnerStyle={{
                    borderRadius: '16px',
                }}
                className="cursor-pointer"
                content={
                    <div className="w-320">
                        <InfusionRatePopup
                            onClose={() => setShowInfusionPopup(false)}
                            dropFlowRate={selectedDropFlowRate}
                            flowRate={selectedFlowRate}
                            flowTime={selectedFlowTime}
                            onChange={handleIvRateChanges}
                        />
                    </div>
                }
                destroyTooltipOnHide={true}
                overlayClassName="popover-p-0"
                zIndex={1050}
                visible={showInfusionPopup}
                onVisibleChange={setShowInfusionPopup}
                getPopupContainer={() =>
                    document.getElementById(INJECTIONS_TABLE_CONTAINER) as HTMLElement
                }
            >
                <div className="hover-parent-element editable-cell-value-wrap" key={columnKey}>
                    <div className="justify-center items-center flex flex-col gap-y-8 px-2 py-8">
                        {!selectedDropFlowRate && !selectedFlowRate && !selectedFlowTime ? (
                            <div className="text-13 font-400 text-darwin-neutral-500">
                                eg. 2 drops/min
                            </div>
                        ) : null}
                        {selectedDropFlowRate && selectedDropFlowRate.split(' ')[0] ? (
                            <div className="text-13 font-400 text-darwin-neutral-1000">
                                {selectedDropFlowRate}
                            </div>
                        ) : null}
                        {selectedFlowRate && selectedFlowRate.split(' ')[0] ? (
                            <div className="text-13 font-400 text-darwin-neutral-1000">
                                {selectedFlowRate}
                            </div>
                        ) : null}
                        {selectedFlowTime && selectedFlowTime.split(' ')[0] ? (
                            <div className="text-12 font-400 text-darwin-neutral-1000">
                                Over {selectedFlowTime}
                            </div>
                        ) : null}
                    </div>
                </div>
            </Popover>
            <InfusionRateCalculator onClick={() => setIsCalculatorOpen(true)} />
            {isCalculatorOpen ? (
                <CalculatorRightPopUp
                    calculatorName={CALCULATORS_NAMES.IV_FLOW_RATE}
                    closeMe={() => setIsCalculatorOpen(false)}
                    onElementUpdate={(option, _, itemIdx) => {
                        if (itemIdx === 0) {
                            handleIvRateChanges({
                                value: `${option.value.qt} ${option.value.unit}`,
                                type: 'iv-drop-rate',
                            });
                        } else if (itemIdx === 1) {
                            handleIvRateChanges({
                                value: `${option.value.qt} ${option.value.unit}`,
                                type: 'iv-rate',
                            });
                        }
                    }}
                />
            ) : null}
        </>
    );
};

const InfusionRateCalculator = ({ onClick }: { onClick: () => void }) => {
    return (
        <div className="p-4 absolute bottom-0 right-0 cursor-pointer" onClick={onClick}>
            <CalculatorRegularIcon className="w-10 h-14 text-darwin-neutral-600" />
        </div>
    );
};

export default InjectionInfusionRate;
